<template>
  <div class="big-table">
    <div class="holder" v-if="table" ref="holder">
      <div class="header-scroller">
        <div class="header" :style="{ transform: `translate(${-scrollLeft}px, 0px)` }">
          <div class="header-row" ref="firstHeadRow">
            <div v-for="(columnName, colId) in table.columnNames"
                 class="cell header-cell"
                 :class="[{
                           'non-numeric': table.columnTypes[colId] != 'number',
                           'sort-desc': sortColumn == columnName && !sortAsc,
                           'sort-asc': sortColumn == columnName && sortAsc
                         }]"
                 :style="{
                         width: colId == 0 ? visibleFirstColumnWidth+'px' : ''
                       }"
                 v-on:click="toggleSort(columnName)">
              {{ table.displayColumnNames[colId] }}
            </div>
          </div>
          <div class="header-row">
            <div class="cell non-numeric">
              Summary:
            </div><div v-for="(columnName, colId) in table.columnNames.slice(1)"
                       class="cell"
                       :class="[
                           {
                             'non-numeric': table.columnTypes[colId+1] != 'number'
                           },
                           generateClass(table.columnNames[colId+1])
                         ]"
                       :style="{
                         width: colId == 0 ? visibleFirstColumnWidth+'px' : ''
                       }">
              {{ cellText(table.summary[colId+1]) }}
            </div>
          </div>
        </div>
      </div>
      <div class="body-holder" v-on:scroll="handleScroll" ref="scroller">
        <div class="body">
          <div :style="{ height: topMargin+'px'}"></div>
          <div v-for="(row, rowId) in renderedRows" :key="rowId+renderedRowsRange.begin" class="row">
            <div v-for="(cell, colId) in row" class="cell"
                 :class="[
                           {
                             'non-numeric': table.columnTypes[colId] != 'number'
                           },
                           generateClass(table.columnNames[colId])
                         ]"
                 :style="{
                   width: colId == 0 ? visibleFirstColumnWidth+'px' : ''
                 }"
                 @mouseover="handleMouseOver" @mouseout="handleMouseOut">
              <span class="expandable">
                <button
                  v-if="colId == 0"
                  class="mdl-button mdl-js-button mdl-button--fab mdl-button--raised mdl-button--accent copy-words"
                  @click="copyText(cellText(cell))"><i class="material-icons">file_copy</i></button>
                {{ cellText(cell) }}
              </span>
            </div>
          </div>
          <div v-if="endInfo && bottomMargin == 0" class="endInfo" :class="endInfo.type">
            <div class="endInfoText">
              {{ endInfo.text }}
            </div>
            <div v-if="endInfo.type == 'download'">
              <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored"
                      v-on:click="$emit('saveCSV')">
                <i class="material-icons">archive</i> {{ i18n().saveCSV }}
              </button>
              <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored"
                      v-on:click="$emit('saveTSV')">
                <i class="material-icons">archive</i> {{ i18n().saveTSV }}
              </button>
            </div>
          </div>
          <div :style="{ height: bottomMargin+'px'}"></div>
        </div>
      </div>
    </div>
    <div class="extender"
         v-if="extender"
         :style="{
           left: extender.x+'px', top: extender.y+'px',
           'min-width': extender.w+'px', 'padding-left': extender.paddingLeft
         }"
         :class="[{ 'non-numeric': extender.nonNumeric }]">{{ extender.text }}</div>
  </div>
</template>

<script>
  import config from "../../config"
  import i18n from "i18n"

  const inivisibleRowsCount = 23
  const clusterSize = 23
  const endInfoHeight = 45

  function fallbackCopyTextToClipboard(text) {
    var textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      var successful = document.execCommand('copy');
      var msg = successful ? 'successful' : 'unsuccessful';
      console.log('Fallback: Copying text command was ' + msg);
    } catch (err) {console.error('Fallback: Oops, unable to copy', err);}
    document.body.removeChild(textArea);
  }
  function copyTextToClipboard(text) {
    if (!navigator.clipboard) {
      fallbackCopyTextToClipboard(text);
      return;
    }
    navigator.clipboard.writeText(text).then(function() {
      console.log('Async: Copying to clipboard was successful!')
    }, function(err) { console.error('Async: Could not copy text: ', err)})
  }

  export default {
    name: 'BigTable2',
    props: {
      table: {
        type: Object
      },
      sortColumn: {
        type: String
      },
      sortAsc: {
        type: Boolean
      },
      found: {
        type: Array
      },
      rowHeight: {
        type: Number,
        default() { return 45 }
      },
      firstColumnWidth: {
        type: Number,
        default() { return 200 }
      },
      showSummary: {
        type: Boolean,
        default: true
      },
      endInfo: {}
    },
    data () {
      return {
        scrollTop: 0,
        scrollLeft: 0,
        scrollHeight: 0,
        visibleFirstColumnWidth: 0,
        savedScrollLeft: 0,
        extender: null
      }
    },
    computed: {
      visibleRowsRange() {
        const firstVisibleRow = (this.scrollTop / this.rowHeight) | 0
        const lastVisibleRow = ((this.scrollTop + this.scrollHeight) / this.rowHeight) | 0
        return { begin: firstVisibleRow, end: lastVisibleRow }
      },
      renderedRowsRange() {
        let { begin, end } = this.visibleRowsRange
        let extendedBegin = Math.max(0, begin - inivisibleRowsCount)
        let extendedEnd = Math.max(0, end + inivisibleRowsCount)
        let extendedBeginCluster = extendedBegin / clusterSize | 0
        let extendedEndCluster = extendedEnd / clusterSize | 0
        let calculatedBegin = extendedBeginCluster * clusterSize
        let calculatedEnd = extendedEndCluster * clusterSize
        return {
          begin: calculatedBegin,
          end: calculatedEnd
        }
      },
      renderedRows() {
        let { begin, end } = this.renderedRowsRange
        return this.table.rows.slice(begin,end)
      },
      topMargin() {
        return Math.max(0, this.renderedRowsRange.begin * this.rowHeight)
      },
      bottomMargin() {
        return Math.max(0, (this.table.rows.length - this.renderedRowsRange.end) * this.rowHeight
          + (this.endInfo ? endInfoHeight : 0))
      }
    },
    watch: {
      table() {
        setTimeout(() => this.resize(), 50)
      },
      renderedRowsRange() {
        setTimeout(() => this.resize(), 50)
      },
    },
    methods: {
      i18n() { return i18n().words },
      cellText(cell) {
        if(cell == Infinity) return "--"//return "∞"
        if(cell == -Infinity) return "--"//return "-∞"
        if(Number.isNaN(cell)) return "--"
        if(typeof cell == "number") return this.addThousandSeparator((cell % 100) == 0 ? cell / 100 : (cell / 100).toFixed(2))
        return cell
      },
      addThousandSeparator(nStr) {
        nStr += '';
        let x = nStr.split('.');
        let x1 = x[0];
        let x2 = x.length > 1 ? '.' + x[1] : '';
        var rgx = /(\d+)(\d{3})/;
        while (rgx.test(x1)) {
          x1 = x1.replace(rgx, '$1' + '\u00A0' + '$2');
        }
        return x1 + x2;
      },
      handleScroll() {
        this.scrollTop = this.$refs.scroller.scrollTop
        this.scrollLeft = this.$refs.scroller.scrollLeft
        this.extender = null
      },
      resize() {
        this.scrollHeight = this.$refs.scroller.clientHeight
        const thead = this.$refs.firstHeadRow
        let otherWidth = 0
        for(let el = thead.firstElementChild.nextElementSibling; el; el = el.nextElementSibling) {
          otherWidth += el.clientWidth
        }
        let spaceWidth = this.$refs.scroller.clientWidth - 5
        let remainingWidth = spaceWidth - otherWidth
        let firstColumnWidth = Math.max(this.firstColumnWidth, remainingWidth)
        console.log("REMAINING WIDTH", remainingWidth, "SPACE WIDTH", spaceWidth, "COLUMNS WIDTH", otherWidth)
        this.visibleFirstColumnWidth = firstColumnWidth
      },
      handleMouseOver(event) {
        return;
        let y = event.target.offsetTop - this.$refs.scroller.clientTop - this.$refs.scroller.scrollTop + this.rowHeight*3
        if(y + this.rowHeight > this.$refs.scroller.clientHeight) {
          y -= this.rowHeight*2
        }
        let x = event.target.offsetLeft - this.$refs.scroller.clientLeft - this.$refs.scroller.scrollLeft + 1 +23
        if(x + 150) {
          x -= 150
        }
        this.extender = {
          x,
          y,
          w: event.target.clientWidth,
          paddingLeft: window.getComputedStyle(event.target, null).getPropertyValue('padding-left'),
          nonNumeric: event.target.classList.contains('non-numeric'),
          text: event.target.innerText
        }
      },
      handleMouseOut(event) {
        this.extender = null
      },
      toggleSort(column) {
        this.$emit('toggleSort', column)
      },
      generateClass(text) {
        if(!text) return "cg-error"
        return text.toLowerCase().replace(/[^a-z]/g, '-')
      },
      copyText(text) {
        copyTextToClipboard(text)
      }
    },
    mounted() {
      this.resizerLoop = () => {
        if(this.finished) return;
        let w = this.$refs.scroller.clientWidth
        let h = this.$refs.scroller.clientHeight
        if(w!= this.width || h != this.height) {
          this.width = w
          this.height = h
          this.resize()
        }
        this.resizerLoopTimeout = setTimeout(this.resizerLoop, 200)
      }
      this.resizerLoop()
    },
    beforeDestroy () {
      if(this.resizerLoopTimeout !== undefined) {
        clearTimeout(this.resizerLoopTimeout)
      }
      this.finished = true
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .big-table {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
  }
  .holder {
    width:100%;
    height: 100%;
    /*overflow-x: scroll;*/
    display: flex;
    flex-direction: column;
  }
  .header-scroller {
    position: relative;
    z-index: 2;
    box-sizing: border-box;
    height: 70px;
    flex-shrink: 0;
    overflow: hidden;
    box-sizing: border-box;
    border-bottom: 1px solid #b4b4b4;
  }
  .header {
    border-left: 1px solid #dddddd;
    border-right: 1px solid #dddddd;
  }
  .body-holder {
    overflow: auto;
  }
  .body {
    border-left: 1px solid #dddddd;
    border-right: 1px solid #dddddd;
    position: relative;
  }

  .row {
    white-space: nowrap;
    display: block;
    flex-direction: row;
    border-bottom: 1px solid #dddddd;
  }
  .row:hover .cell {
    background: #e8e8e8;
  }
  .header-row {
    white-space: nowrap;
    display: block;
    flex-direction: row;
  }
  .cell {
    display: inline-block;
    box-sizing: border-box;
    width: 100px;
    height: 35px;
    line-height: 27px;
    background: white;
    padding: 5px;
    vertical-align: top;
    text-align: right;
    /*text-overflow: ellipsis;
    overflow: hidden;*/
    overflow: visible;
    position: relative;
  }
  .cell.non-numeric {
    text-align: left;
  }
  .cell:nth-child(1) {
    padding-left: 20px;
  }
  .cell:last-child {
    padding-right: 20px;
    width: 120px;
  }
  .cell:last-child .expandable {
    padding-right: 20px;
  }


  .header .cell.ctr:after {
    content: "%"
  }
  .header .cell.roc:after {
    content: "%"
  }
  .header .cell.cr:after {
    content: "%"
  }

  .cell.ctr .expandable:after {
    content: "%"
  }
  .cell.roc .expandable:after {
    content: "%"
  }
  .cell.cr .expandable:after {
    content: "%"
  }

  .extender {
    border: 1px solid #dddddd;
    border-radius: 23px;
    position: absolute;
    box-sizing: border-box;
    height: 35px;
    line-height: 27px;
    padding: 5px;
    background: white;
    pointer-events: none;
    text-align: right;
  }
  .extender.non-numeric {
    text-align: left;
  }

  .header-cell {
    font-weight: bold;
    font-size: 12px;
    color: #8c8c8c;
    cursor: pointer;
  }
  .header-cell:hover {
    color: black;
  }

  .sort-desc:before {
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #8c8c8c;
    content:"";
    position: relative;
    top: 10px;
    right:3px;
  }
  .sort-asc:before {
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid #8c8c8c;
    content:"";
    position: relative;
    top: -12px;
    right:3px;
  }

  .cell .expandable {
    display:block;
    position: absolute;
    padding: 5px;
    box-sizing: border-box;
    top: 0;
    right: 0;
    left: auto;
    width: 100%;
    height: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    background: white;
  }

  .row:hover .cell .expandable {
    background: #e8e8e8;
  }

  .cell.non-numeric .expandable {
    left: 0;
    width: 100%;
    right: auto;
  }

  .cell .expandable:hover {
    width: auto !important;
    overflow: hidden;
    z-index: 1;
  }


  .endInfo {
    height: 45px;
    text-align: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }
  .endInfo > div {
    vertical-align: middle;
  }
  .endInfoText {
    margin-right: 2em;
  }

  button.copy-words {
    width: 22px;
    max-width: 22px;
    min-width: 22px;
    height: 22px;
    margin-right: 4px;
  }
  button.copy-words > i {
    font-size: 14px;
  }

</style>



// WEBPACK FOOTER //
// BigTable2.vue?97c9c37e